import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

interface TrendVelocityChartProps {
  topicTitle: string;
  velocityData: number[];
}

const TrendVelocityChart: React.FC<TrendVelocityChartProps> = ({
  topicTitle,
  velocityData,
}) => {
  // Day labels for X-axis
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Generate mock previous week data (for comparison)
  const previousWeekData = velocityData.map((val) =>
    Math.max(0, val - Math.random() * 20 + 10)
  );

  // Transform data for Recharts
  const chartData = dayLabels.map((day, index) => ({
    day,
    current: velocityData[index] || 0,
    previous: previousWeekData[index] || 0,
  }));

  // Trending threshold value
  const trendingThreshold = 60;

  // Custom tooltip component with glassmorphism styling
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="rounded-lg border border-white/20 p-3 shadow-xl"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <p className="mb-2 font-semibold text-white">{label}</p>
          <div className="space-y-1">
            <p className="text-sm text-cyan-300">
              Current Week: <span className="font-bold">{payload[0].value.toFixed(1)}</span>
            </p>
            <p className="text-sm text-violet-300">
              Previous Week: <span className="font-bold">{payload[1].value.toFixed(1)}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 shadow-2xl">
      {/* Chart Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white">Search Velocity Index</h3>
          <p className="mt-1 text-sm text-slate-400">{topicTitle}</p>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-green-500/20 px-3 py-1.5">
          <div className="h-2 w-2 animate-pulse rounded-full bg-green-400"></div>
          <span className="text-xs font-semibold text-green-400">LIVE DATA</span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            {/* Gradient for current week (cyan) */}
            <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.1} />
            </linearGradient>
            {/* Gradient for previous week (violet) */}
            <linearGradient id="colorPrevious" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.6} />
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
          <XAxis
            dataKey="day"
            stroke="#94a3b8"
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
          />
          <YAxis
            domain={[0, 100]}
            stroke="#94a3b8"
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            axisLine={{ stroke: '#475569' }}
            label={{
              value: 'Search Volume Index',
              angle: -90,
              position: 'insideLeft',
              style: { fill: '#94a3b8', fontSize: 12 },
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
            }}
            iconType="line"
            formatter={(value) => (
              <span className="text-sm text-slate-300">
                {value === 'current' ? 'Current Week' : 'Previous Week'}
              </span>
            )}
          />
          {/* Reference line for trending threshold */}
          <ReferenceLine
            y={trendingThreshold}
            stroke="#f59e0b"
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{
              value: 'Trending Threshold',
              position: 'right',
              fill: '#f59e0b',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
          {/* Previous week area (violet/dim) */}
          <Area
            type="monotone"
            dataKey="previous"
            stroke="#8b5cf6"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorPrevious)"
          />
          {/* Current week area (cyan) */}
          <Area
            type="monotone"
            dataKey="current"
            stroke="#06b6d4"
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#colorCurrent)"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Chart Footer Stats */}
      <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-700 pt-4">
        <div className="text-center">
          <p className="text-xs text-slate-400">Peak Velocity</p>
          <p className="mt-1 text-lg font-bold text-cyan-400">
            {Math.max(...velocityData).toFixed(1)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-400">Average</p>
          <p className="mt-1 text-lg font-bold text-violet-400">
            {(velocityData.reduce((a, b) => a + b, 0) / velocityData.length).toFixed(1)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-400">Growth Rate</p>
          <p className="mt-1 text-lg font-bold text-green-400">
            +{((velocityData[6] - velocityData[0]) / velocityData[0] * 100).toFixed(1)}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrendVelocityChart;